import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { motion, useReducedMotion } from "motion/react";

import { cn } from "@/lib/utils";

/**
 * Der gleitende Indikator braucht zweierlei, was Radix nicht herausgibt: den
 * gerade aktiven Wert und eine gemeinsame `layoutId` für alle Reiter einer
 * Leiste. Beides trägt dieser Kontext; die Bedienung selbst bleibt bei Radix.
 */
const TabsIndicatorContext = React.createContext<{
    value: string | undefined;
    layoutId: string;
} | null>(null);

const Tabs = ({
    value,
    defaultValue,
    onValueChange,
    ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>) => {
    const layoutId = React.useId();
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const currentValue = value ?? internalValue;
    const indicator = React.useMemo(
        () => ({ value: currentValue, layoutId }),
        [currentValue, layoutId]
    );

    return (
        <TabsIndicatorContext.Provider value={indicator}>
            <TabsPrimitive.Root
                value={value}
                defaultValue={defaultValue}
                onValueChange={(next) => {
                    // Ungesteuerte Leisten führen ihren Wert bei Radix; für den
                    // Indikator muss er hier mitlaufen.
                    if (value === undefined) setInternalValue(next);
                    onValueChange?.(next);
                }}
                {...props}
            />
        </TabsIndicatorContext.Provider>
    );
};
Tabs.displayName = TabsPrimitive.Root.displayName;

// Default style is the "line" / underline-tab look used across the app:
// TabsList sits on a single bottom border; the active TabsTrigger overlays
// that line with a 2px foreground-coloured border. Pass `className` to
// override on a per-call-site basis if a different look is needed.
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} asChild {...props}>
    {/* `layoutRoot`: Die `layoutId` des Indikators misst in Seitenkoordinaten.
        In einer scrollenden Seite spielte er sonst den Scrollweg als Bewegung
        nach. Der Strich wandert ohnehin nur innerhalb der Leiste. */}
    <motion.div
      layoutRoot
      className={cn(
        // overflow-x-auto: viele Reiter (z. B. 11 auf der Gebäudeseite) scrollen
        // in der Leiste, statt das Panel horizontal aufzudrücken.
        "inline-flex h-auto w-full items-center justify-start gap-6 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 text-muted-foreground",
        className
      )}
    >
      {children}
    </motion.div>
  </TabsPrimitive.List>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const indicator = React.useContext(TabsIndicatorContext);
  const reduce = useReducedMotion();
  const active = indicator?.value !== undefined && indicator.value === props.value;

  return (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // No focus ring: a tab is not an input or a plain button, and with Radix's
      // automatic activation the focused tab is always the active one — the
      // `data-[state=active]` border and text colour already show where focus is.
      // `outline-none` stays, otherwise the browser default draws the ring instead.
      // Der Rand bleibt durchsichtig und hält nur den Platz frei; sichtbar
      // macht den aktiven Reiter der gleitende Strich darunter.
      "relative -mb-px inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none border-b-2 border-transparent bg-transparent px-1 pb-2 pt-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none",
      className
    )}
    {...props}
  >
    {children}
    {active ? (
      <motion.span
        aria-hidden
        layoutId={indicator?.layoutId}
        layout="position"
        transition={
          reduce
            ? { duration: 0 }
            : { type: "spring", stiffness: 170, damping: 24, mass: 1.2 }
        }
        className="absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-foreground"
      />
    ) : null}
  </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // Radix makes the panel focusable so keyboard users land on the content, but
      // it is a content region, not a control — ringing it drew a box around the
      // whole tab body. Keep it focusable, just not outlined.
      "mt-2 focus-visible:outline-none",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
